import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DNAMassCalculator = () => {
  const [sequence, setSequence] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Nucleotide molecular weights (in g/mol)
  const nucleotideWeights = {
    'A': 313.21, // Deoxyadenosine monophosphate
    'T': 304.2,  // Deoxythymidine monophosphate
    'G': 329.21, // Deoxyguanosine monophosphate
    'C': 289.18  // Deoxycytidine monophosphate
  };

  const handleInputChange = (e) => {
    setSequence(e.target.value.toUpperCase());
    setError('');
  };

  const calculateMass = () => {
    if (!sequence) {
      setError('Please enter a DNA sequence');
      return;
    }

    const sequenceArray = sequence.split('');
    let totalMass = 0;
    const nucleotideCounts = {};
    const invalidNucleotides = [];
    const calculationSteps = [];

    // Count nucleotides and validate sequence
    calculationSteps.push('Step 1: Calculating individual nucleotide masses');
    const individualMasses = [];
    for (let nt of sequenceArray) {
      if (nucleotideWeights[nt]) {
        totalMass += nucleotideWeights[nt];
        nucleotideCounts[nt] = (nucleotideCounts[nt] || 0) + 1;
        individualMasses.push(`${nt}: ${nucleotideWeights[nt]} g/mol`);
      } else {
        invalidNucleotides.push(nt);
      }
    }
    calculationSteps.push(`Individual nucleotide masses:\n${individualMasses.join('\n')}`);
    calculationSteps.push(`Sum of nucleotide masses: ${totalMass.toFixed(2)} g/mol`);

    if (invalidNucleotides.length > 0) {
      setError(`Invalid nucleotides found: ${invalidNucleotides.join(', ')}`);
      return;
    }

    // Calculate phosphodiester bond adjustments
    calculationSteps.push('\nStep 2: Calculating phosphodiester bond adjustments');
    const bondAdjustment = 17.01; // Mass adjustment for each phosphodiester bond (g/mol)
    const bonds = sequenceArray.length - 1;
    calculationSteps.push(`Number of phosphodiester bonds: ${bonds}`);
    calculationSteps.push(`Mass adjustment per bond: ${bondAdjustment} g/mol`);
    calculationSteps.push(`Total bond adjustment: ${(bondAdjustment * bonds).toFixed(2)} g/mol`);

    const finalMass = totalMass - (bondAdjustment * bonds);
    calculationSteps.push('\nStep 3: Calculating final DNA mass');
    calculationSteps.push(`Final mass = Sum of nucleotide masses - (bond adjustment × number of bonds)`);
    calculationSteps.push(`Final mass = ${totalMass.toFixed(2)} - (${bondAdjustment} × ${bonds})`);
    calculationSteps.push(`Final mass = ${finalMass.toFixed(2)} g/mol`);

    const newResults = {
      totalMass: finalMass.toFixed(2),
      sequenceLength: sequenceArray.length,
      nucleotideComposition: nucleotideCounts,
      calculationSteps: calculationSteps
    };

    setResults(newResults);

    // Update chart data
    const sortedCounts = Object.entries(nucleotideCounts).sort((a, b) => b[1] - a[1]);
    setChartData({
      labels: sortedCounts.map(([nt]) => nt),
      datasets: [{
        label: 'Nucleotide Composition',
        data: sortedCounts.map(([, count]) => count),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    });
  };

  const exampleData = {
    sequence: 'ATGCTAGCTA',
    mass: 3024.21,
    length: 10,
    composition: {
      'A': 3,
      'T': 2,
      'G': 2,
      'C': 3
    }
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">DNA Mass Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Molecular Weight Calculation</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate the molecular mass of DNA based on its nucleotide sequence. 
          Enter the sequence using standard nucleotide codes (A, T, C, G). The calculator 
          accounts for phosphodiester bond formation between nucleotides.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="sequence">
              DNA Sequence
            </label>
            <textarea
              id="sequence"
              name="sequence"
              value={sequence}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter DNA sequence (e.g., ATGC)"
              rows="4"
            />
          </div>

          <button
            onClick={calculateMass}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Calculate DNA Mass
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}
        </div>

        {results && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Molecular Mass</h3>
                <p className="text-2xl font-medium">{results.totalMass} g/mol</p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Sequence Information</h3>
                <p className="text-lg font-medium">{results.sequenceLength} nucleotides</p>
              </div>
            </div>

            {chartData && (
              <div className="bg-white p-6 rounded-xl shadow-md mb-12">
                <h3 className="text-xl font-semibold mb-4">Nucleotide Composition</h3>
                <div className="h-96">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Count'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Nucleotide'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Nucleotide Composition'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Calculation Steps</h3>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using a sample DNA sequence:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample DNA Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Sequence: {exampleData.sequence}</li>
              <li>Length: {exampleData.length} nucleotides</li>
              <li>Molecular Mass: {exampleData.mass} g/mol</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculation Steps:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Sum the masses of individual nucleotides</p>
              </li>
              <li>
                <p>Account for phosphodiester bond formation
                  <ul className="list-none pl-8 space-y-2">
                    <li>Mass adjustment of 17.01 g/mol per bond</li>
                    <li>Number of bonds = sequence length - 1</li>
                  </ul>
                </p>
              </li>
              <li>
                <p>Final mass = Sum of nucleotide masses - (bond adjustment × number of bonds)</p>
              </li>
            </ol>

            <h4 className="font-semibold mt-6 mb-4">Nucleotide Composition:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(exampleData.composition).map(([nt, count]) => (
                <div key={nt} className="bg-blue-100 p-3 rounded-lg text-center">
                  <p className="font-medium">{nt}</p>
                  <p>{count}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the DNA Mass Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Prepare Your DNA Sequence</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Ensure your sequence is in single-letter DNA code (A, T, C, G)</li>
                <li>Remove any spaces or special characters</li>
                <li>Use uppercase letters for standard bases</li>
                <li>Include modifications if present (e.g., phosphorothioate bonds)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Sequence Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Paste or type your DNA sequence into the input field</li>
                <li>Select the appropriate DNA type (ssDNA, dsDNA, RNA)</li>
                <li>Enter the concentration if known</li>
                <li>Click the "Calculate DNA Mass" button</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated DNA mass</li>
                <li>Check the base composition breakdown</li>
                <li>Verify the sequence length</li>
                <li>Use the composition chart for visual analysis</li>
                <li>Compare results with experimental data</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Important Considerations</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Ensure correct DNA base codes are used</li>
                <li>Account for modifications in your sequence</li>
                <li>Verify sequence length matches expectations</li>
                <li>Compare calculated mass with experimental data</li>
                <li>Use the composition chart to identify unusual patterns</li>
                <li>Consider salt concentration effects on mass</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding DNA Mass <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            The molecular mass of DNA is calculated by:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Adding nucleotide masses:</span> Each nucleotide has a specific molecular weight
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Accounting for phosphodiester bonds:</span> Mass adjustment for each bond formation
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Considering modifications:</span> Post-synthetic modifications can affect the final mass
            </li>
          </ul>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Tips for Using the Calculator <span className="ml-2 text-2xl">💡</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Use standard one-letter codes for nucleotides</span>
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Enter the sequence without spaces or special characters</span>
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Double-check your sequence for accuracy</span>
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Consider DNA modifications separately</span>
            </li>
          </ul>
          <p className="mt-6 italic text-gray-500">
            Note: This calculator provides the theoretical mass based on the primary sequence only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DNAMassCalculator;